<template>
  <div>
    <div v-if="loading">
      <Preloader />
    </div>
    <div v-else-if="errorMessage">
      <VAlert type="error">
        {{ errorMessage }}
      </VAlert>
    </div>
    <div v-else>
      <VacancyStepTitle
        v-if="vacancy"
        :progress="60"
        :vacancy="vacancy"
        :step="3"
      >
        {{ $t('step') }}
      </VacancyStepTitle>
      <div class="mt-2 mt-md-0">
        <a @click="goToWizard" class="link-dotted">
          {{ $t('returnToTemplates') }}
        </a>
      </div>
      <VacancyWizardCategory
        @selectCategory="selectCategory"
        v-if="commonCategory"
        :category="selectedCategory"
      />
      <div class="d-flex mt-3 mt-md-6 pb-4">
        <VBtn
          class="mr-auto mr-md-2"
          color="success"
          outlined
          @click="back"
        >
          {{ $t('back') }}
        </VBtn>
        <VBtn color="success" elevation="0" @click="next">
          {{ $t('next') }} &rarr;
        </VBtn>
        <WizardCounts
          v-if="$vuetify.breakpoint.mdAndUp"
          :questions="selectedQuestions.length"
          :is-ai-interviewer="isAiInterviewer"
          class="ml-auto mr-2 mb-0"
        />
        <VBtn
          v-if="isCompleted"
          class="ml-2"
          color="success"
          outlined
          @click="end"
        >
          {{ $t('end') }}
        </VBtn>
      </div>
    </div>
  </div>
</template>

<script>
import VacancyStepTitle from '@/views/Vacancy/VacancyStepTitle';
import Vacancy from '@/orm/models/Vacancy/Vacancy';
import ValidationMixin from '@/mixins/ValidationMixin';
import InterviewVideoCategoryVideo from '@/orm/models/Interview/InterviewVideoCategoryVideo';
import Preloader from '@/components/Preloader';
import VacancyWizardCategory from '@/components/Vacancy/VacancyWizardCategory';
import { findObject } from '@/functions';
import { questionCategoryType, wizardSteps } from '@/enums';
import WizardCounts from '@/components/Vacancy/WizardCounts';
import Wizard from '@/orm/models/Wizard/Wizard';

export default {
  name: 'VacancyStepWizardCommon',
  components: {
    WizardCounts,
    VacancyWizardCategory,
    Preloader,
    VacancyStepTitle,
  },
  mixins: [ValidationMixin],
  props: {
    vacancyId: {
      type: Number,
      required: true,
    },
    interviewerId: {
      type: Number,
      required: true,
    },
    categoryId: {
      type: Number,
      default: 0,
    },
    index: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      loading: true,
    };
  },
  computed: {
    categories() {
      return InterviewVideoCategoryVideo.query().withAllRecursive().all();
    },
    vacancy() {
      return Vacancy.query().find(this.vacancyId);
    },
    commonCategory() {
      return this.categories.find((category) => category.type === questionCategoryType.COMMON);
    },
    selectedCategoryId() {
      return this.categoryId || this.commonCategory.children[this.index].id;
    },
    canNextIndex() {
      return this.commonCategory.children.length > this.index + 1;
    },
    isLastIndex() {
      return this.commonCategory.children.length === this.index + 1;
    },
    canPrevIndex() {
      return this.index - 1 > -1 || this.categoryId > 0;
    },
    selectedQuestions() {
      return Wizard.modelState().selectedQuestions;
    },
    selectedCategory() {
      return findObject(
        (category) => category.id === this.selectedCategoryId,
        this.commonCategory,
        'children',
      );
    },
    isCompleted() {
      return Wizard.modelState().isCompleted || !!this.vacancy.script?.questions?.length;
    },
    isAiInterviewer() {
      return this.vacancy?.wizardState.isAiInterviewer;
    },
  },
  created() {
    this.setState();
    this.withLoading(
      async () => {
        await Wizard.fetchData();
      },
    );
  },
  methods: {
    setState() {
      Wizard
        .setVacancyId(this.vacancyId)
        .setInterviewerId(this.interviewerId)
        .setWizardStep(this.categoryId ? wizardSteps.CATEGORY : wizardSteps.COMMON)
        .setCategory(this.categoryId)
        .setIndex(this.index)
        .setIsAiInterviewer(this.isAiInterviewer);
    },
    async submit() {
      this.setState();
      await Wizard.saveWizardState();
    },
    async end() {
      await this.submit();
      if (this.vacancy.script?.questions?.length) {
        await this.$router.push({
          name: 'vacancy-questions-edit',
          params: {
            id: this.vacancyId,
          },
        });
      } else {
        await this.$router.push({
          name: 'vacancy-questions',
          params: {
            vacancyId: this.vacancyId,
            interviewerId: this.interviewerId,
          },
        });
      }
    },
    async next() {
      await this.submit();
      await Wizard.pushNextRoute(this.$router, { count: this.commonCategory.children.length });
    },
    async back() {
      if (!this.canPrevIndex && this.vacancy.script?.questions?.length) {
        await this.$router.push(
          {
            name: 'vacancy-questions-edit',
            params: {
              id: this.vacancy.id,
            },
          },
        );
      } else {
        Wizard.setIndex(this.index);
        await Wizard.pushPrevRoute(this.$router);
      }
    },
    async selectCategory(category) {
      await this.$router.push({
        name: 'vacancy-wizard-category',
        params: {
          vacancyId: this.vacancyId,
          interviewerId: this.interviewerId,
          index: this.index,
          categoryId: category.id,
        },
      });
    },
    async goToWizard() {
      await this.$router.push({
        name: 'vacancy-preset',
        params: {
          vacancyId: this.vacancyId,
        },
      });
    },
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => vm.setState());
  },
  beforeRouteUpdate(to, from, next) {
    this.setState();
    next();
  },
};
</script>

<style scoped>

</style>

<i18n locale="en" lang="yaml">
  step: "questions list"
  back: Back
  next: Next
  end: Complete
  returnToTemplates: Go back to the Template Selection Wizard
</i18n>

<i18n locale="ru" lang="yaml">
  step: "список вопросов для интервью"
  back: Назад
  next: Далее
  end: Завершить
  returnToTemplates: Вернуться в мастер выбора шаблона
</i18n>
